import {Component} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {
  DatatableActionButton,
  DatatableEvent,
  DatatableHandler,
  DatatableHandlerEvents,
  DatatableHandlerSettings,
  IconType,
} from '../../../components/datatables/datatable/datatable/datatable.component';
import { SimpleModalService } from 'ngx-simple-modal';
import { ConfirmModalComponent } from '../../../components/modals/confirm-modal/confirm-modal.component';
import { ExamReportsClient } from '../../../generated-client/generated-api-client';
import { ToastrService } from 'ngx-toastr';
import { DataTableRoutes } from 'src/app/constants/datatableUrls';
import { ErrorProcessorService } from '../../../services/error-processor.service';
import { DateTime } from 'luxon';
import { TenantService } from '../../../services/tenant.service';
import { ConfirmReasonModalComponent } from "../../../components/modals/archive-confirm-modal/confirm-reason-modal.component";
import { PersistentDataService } from '../../../services/persistent-data.service';

@Component({
  selector: 'app-exam-report-list',
  templateUrl: './exam-report-list.component.html',
  styleUrls: ['./exam-report-list.component.sass'],
})
export class ExamReportListComponent {
  private _dtActionButtonsFn: (data: any) => DatatableActionButton[] = data => {
    const id = data['id'];
    const isFinished = data['isFinished'];
    const isSyncEnabled = data['isSyncEnabled'];

    const editUrl = `/exam-reports/${id}/exam-report-details`;

    const examStarted = DateTime.fromISO(data['examStartedAtUtc']);
    const examEnded = DateTime.fromISO(data['examEndedAtUtc']);
    const realExamEnded = DateTime.fromISO(data['realExamEndedAtUtc']);

    const editAction = (isFinished || realExamEnded > examStarted)
      ? new DatatableActionButton('details', 'Jegyzőkönyv megtekintése', 'visibility', editUrl)
      : new DatatableActionButton('edit', 'Jegyzőkönyv szerkesztése', 'edit', editUrl);

    const syncAction = isSyncEnabled
      ? new DatatableActionButton('sync', 'Szinkronizáció', 'sync_alt')
      : new DatatableActionButton('sync', 'Szinkronizáció', 'sync_alt', undefined, IconType.Normal, true);

    const isExamRunning = examStarted < DateTime.now() && examEnded > DateTime.now();
    const isDeleteDisabled = isExamRunning || isFinished;

    const deleteAction = isDeleteDisabled
      ? new DatatableActionButton('delete', 'Jegyzőkönyv törlése', 'delete', undefined, IconType.Danger, true)
      : new DatatableActionButton('delete', 'Jegyzőkönyv törlése', 'delete', undefined, IconType.Danger);
    return [
      editAction,
      syncAction,
      deleteAction
    ];
  };

  dtHandler?: DatatableHandler;
  dtSettings: DatatableHandlerSettings = DatatableHandler.getDefaultSettings();
  dtEvents: DatatableHandlerEvents = DatatableHandler.getDefaultEvents();
  dtDataSourceUrl: string = DataTableRoutes.ExamReportList;

  constructor(
    private _titleService: Title,
    private _modalService: SimpleModalService,
    private _examReportsClient: ExamReportsClient,
    private toastr: ToastrService,
    private errorProcessorService: ErrorProcessorService,
    private _tenantService: TenantService,
    private _pDservice: PersistentDataService
  ) {
    this._titleService.setTitle('Jegyzőkönyvek');
    this.dtSettings.order = [4, 'desc'];
    this.dtSettings.buttons = [
      {
        text: 'Új jegyzőkönyv',
        class: 'btn-primary text-white',
        url: '/exam-reports/new',
      },
    ];
    this.dtSettings.actions = this._dtActionButtonsFn;

    this.dtSettings.pageSize = _pDservice.examReportsPageSize;
    this.dtSettings.searchString = _pDservice.examReportsSearchString;

    this.dtEvents.createdRow = (row: Node, data: any) => {
      if (data['isArchived']) {
        let rowElement = row as HTMLTableRowElement;
        rowElement.classList.add('archived');
      }
    };

    this.dtSettings.additionalRequestData = () => {
      if (this.dtHandler?.pageSize != null)
        _pDservice.examReportsPageSize = this.dtHandler?.pageSize
      if (this.dtHandler?.searchString != null)
        _pDservice.examReportsSearchString = this.dtHandler?.searchString
      return {
        tenantId: this._tenantService.activeTenantId,
      };
    };

    this._tenantService.tenantId.subscribe(tenantId => {
      this.dtHandler?.refresh();
    });
  }

  onInit(dtHandler: DatatableHandler) {
    this.dtHandler = dtHandler;
    this.dtHandler.create();
  }

  onAction(event: DatatableEvent) {
    if (event.actionId === 'delete') {
      const examStartedAtUtc = DateTime.fromISO(event.data['examStartedAtUtc']);
      const examEndedAtUtc = DateTime.fromISO(event.data['examEndedAtUtc']);
      const realExamEndedAtUtc = DateTime.fromISO(event.data['realExamEndedAtUtc']);
      const isExamRunning = examStartedAtUtc < DateTime.now() && (realExamEndedAtUtc ?? examEndedAtUtc) > DateTime.now();
      if (isExamRunning) {
        this.toastr.error('Jelenleg futó vizsga jegyzőkönyve nem törölhető!');
        return;
      } else if (event.data['isArchived'] === true) {
        this.toastr.error('Archivált jegyzőkönyv nem törölhető!');
        return;
      } else {
        this.delete(event.data['id']);
      }
    }
    if (event.actionId === 'sync') {
      this.sync(event.data['id']);
    }
  }

  private delete(id: string): void {
    this._modalService
      .addModal(ConfirmReasonModalComponent, {
        title: 'Jegyzőkönyv törlése',
        message: 'Biztosan törli a jegyzőkönyvet?',
        confirmText: 'Törlés',
        confirmClass: 'btn-danger text-white',
      })
      .subscribe(result => {
        if (result) {
          this._examReportsClient.delete(id, result.reason).subscribe(
            () => {
              this.dtHandler?.refresh();
              this.toastr.success('Sikeres törlés');
            },
            error => this.errorProcessorService.processError(error, true)
          );
        }
      });
  }

  private sync(id: string): void {
    this.toastr.info('Szinkronizáció megkezdve');
    this._examReportsClient.syncPost(id).subscribe(
      () => {
        this.dtHandler?.refresh();
        this.toastr.success('Sikeres szinkronizáció');
      },
      error => this.errorProcessorService.processError(error)
    );
  }
}
